<template>
<v-dialog v-model="show" max-width="800" scrollable>
  <template v-slot:activator="{on}">
    <slot name="activator" :on="on">
      <v-btn v-on="on" color="info">Create Customer</v-btn>
    </slot>
  </template>
  <v-card>
    <CardTitle title="Create Customer"></CardTitle>
    <v-card-text>
      <v-alert type="info" class="mb-0" outlined tile dense>Make sure the customer you are creating haven't already filled out a form in <a href="https://results.conflabs.com" target="_blank">results</a></v-alert>
      <v-form ref="form">
        <v-text-field v-model="form.primary_name" label="Primary Tradename" :rules="[required]" filled dense></v-text-field>
        <v-card class="pa-0 pl-4" flat>
          <v-list-item v-for="(name, index) in form.names" :key="index">
                <v-text-field v-model="name.name" label="Name" :rules="[required]" dense></v-text-field>
                <v-spacer></v-spacer>
                <v-autocomplete v-model="name.name_type_id" :items="$store.$db().model('nameTypes').all()" item-text="desc" item-value="id" label="Type" :rules="[required]" dense></v-autocomplete>
                <v-spacer></v-spacer>
                <v-list-item-action>
                  <v-btn @click="$delete(form.names, index)" color="error" tile x-small><v-icon>mdi-minus</v-icon></v-btn>
                </v-list-item-action>
                <v-spacer></v-spacer>
          </v-list-item>
          <v-card-actions>
            <v-btn @click="addName" color="primary" small tile>Add additional names</v-btn>
          </v-card-actions>
        </v-card>
        <v-divider></v-divider>
        <v-card class="pa-0 pl-4" flat>
          <v-alert type="error"  v-if="licensesCheckResults.length > 0">
            <v-row no-gutters>
              <v-col cols="12" v-for="(license, index) in licensesCheckResults" :key="index">
                <strong>{{ license.license }}</strong> already exists for <a :href="`/directory_V2/${license.customer_id}`" class="white--text" target="_blank"><strong>{{ license.customer.primary_name.name }}</strong></a>.
              </v-col>
            </v-row>
          </v-alert>
          <v-list-item v-for="(license, index) in form.licenses" :key="index">
            <v-text-field v-model="license.license" @keyup="debounceLicensesCheck" label="License" :rules="[required]" dense></v-text-field>
            <v-spacer></v-spacer>
            <v-autocomplete v-model="license.license_type_id" :items="$store.$db().model('licenseTypes').all()" item-text="desc" item-value="id" label="Type" :rules="[required]" dense></v-autocomplete>
            <v-spacer></v-spacer>
            <v-list-item-action>
              <v-btn @click="$delete(form.licenses, index);debounceLicensesCheck()" color="error" tile x-small><v-icon>mdi-minus</v-icon></v-btn>
            </v-list-item-action>
            <v-spacer></v-spacer>
          </v-list-item>
          <v-card-actions>
            <v-btn @click="addLicense" color="primary" small tile>Add License</v-btn>
          </v-card-actions>
        </v-card>
        <v-divider></v-divider>
        <v-list-item>
          <v-autocomplete v-model="form.environment_type_id" :items="$store.$db().model('environmentTypes').all()" item-text="desc" item-value="id" label="Environment" :rules="[required]" filled dense></v-autocomplete>
          <v-spacer></v-spacer>
          <v-autocomplete v-model="form.contract_type_id" :items="$store.$db().model('contractTypes').all()" item-text="desc" item-value="id" label="Contract" :rules="[required]" filled dense></v-autocomplete>
        </v-list-item>
        <v-divider></v-divider>
        <v-card flat>
          <v-card-title class="font-weight-bold">Contacts</v-card-title>
          <template v-for="(contact, index) in form.contacts">
            <v-card class="mb-2" :key="index">
              <v-list>
                <v-list-item>
                  <v-autocomplete v-model="contact.contact_type_id" :items="$store.$db().model('contactTypes').all()" item-text="desc" item-value="id" :rules="[required]" label="Contact Type" filled dense></v-autocomplete>
                  <template v-if="index !== 0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{on}">
                        <v-btn color="error" class="ml-4 mr-4" v-on="on" @click="$delete(form.contacts, index)" tile x-small><v-icon>mdi-minus</v-icon></v-btn>
                      </template>
                      <span>Remove Contact</span>
                    </v-tooltip>
                  </template>
                </v-list-item>
                <v-list-item>
                  <v-text-field v-model="contact.name" :rules="[required]" label="Name" dense></v-text-field>
                  <v-spacer></v-spacer>
                  <v-text-field v-model="contact.email" :rules="[required]" label="Email" dense></v-text-field>
                </v-list-item>
                <v-list-item>
                  <v-text-field v-model="contact.alt_email" label="Alternative Email"></v-text-field>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold"> Email Subscriptions</v-list-item-title>
                  </v-list-item-content>
                  <v-btn-toggle v-model="contact.subscriptions" multiple active-class="success">
                    <v-tooltip top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" icon :value="2" small><v-icon>mdi-credit-card</v-icon></v-btn>
                      </template>
                      <span>Billing</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" icon :value="3" small><v-icon>mdi-truck</v-icon></v-btn>
                      </template>
                      <span>Drive Calendar</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" icon :value="5" small><v-icon>mdi-check</v-icon></v-btn>
                      </template>
                      <span>Results</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" icon :value="7" small><v-icon>mdi-bomb</v-icon></v-btn>
                      </template>
                      <span>Advertisements</span>
                    </v-tooltip>
                  </v-btn-toggle>
                  <v-spacer></v-spacer>
                  <v-spacer></v-spacer>
                </v-list-item>
              </v-list>
              <v-list>
                <v-list-item v-for="(number, key) in contact.phone_numbers" :key="key">
                  <v-text-field label="number" v-model="number.number" :rules="[required]"></v-text-field>
                  <v-spacer></v-spacer>
                  <v-text-field label="Extension" v-model="number.ext"></v-text-field>
                  <v-spacer></v-spacer>
                  <v-btn @click="$delete(contact.phone_numbers, key)" color="error" tile x-small><v-icon>mdi-minus</v-icon></v-btn>
                  <v-spacer></v-spacer>
                </v-list-item>
              </v-list>
              <v-card-actions>
                <v-btn @click="addPhoneNumber(index)" color="info" small tile>add Phone Number</v-btn>
              </v-card-actions>
            </v-card>
          </template>
          <v-card-actions>
            <v-btn @click="addContact" color="primary" small tile>Add Another Contact</v-btn>
          </v-card-actions>
        </v-card>
        <v-divider></v-divider>
        <v-card flat>
          <v-card-title>Address</v-card-title>
          <v-list>
            <v-list-item>
              <v-autocomplete v-model="form.address.address_type_id" :items="$store.$db().model('addressTypes').all()" item-text="desc" item-value="id" label="Address Type" :rules="[required]"></v-autocomplete>
              <v-spacer></v-spacer>
              <v-autocomplete v-model="form.address.region_type_id" :items="$store.$db().model('regionTypes').all()" item-text="desc" item-value="id" label="Region" :rules="[required]"></v-autocomplete>
            </v-list-item>
            <v-list-item>
              <v-text-field v-model="form.address.street" label="Street" :rules="[required]"></v-text-field>
            </v-list-item>
            <v-list-item>
              <v-text-field v-model="form.address.city" label="City" :rules="[required]"></v-text-field>
              <v-spacer></v-spacer>
              <v-text-field v-model="form.address.state" label="State" :rules="[required]"></v-text-field>
              <v-spacer></v-spacer>
              <v-text-field v-model="form.address.zipcode" label="Zipcode" :rules="[required]"></v-text-field>
            </v-list-item>
          </v-list>
        </v-card>
        <v-divider></v-divider>
        <v-card class="mt-2" flat>
          <template v-if="form.notes.length > 0">
            <v-card-title>Notes</v-card-title>
            <v-card-text v-for="(note, index) in form.notes">
              <v-list-item class="pl-0">
                <v-autocomplete v-model="note.note_type_id" :items="$store.$db().model('noteTypes').all()" item-text="desc" item-value="id" label="Note type" :rules="[required]"></v-autocomplete>
                <v-spacer></v-spacer>
                <v-btn color="error" @click="$delete(form.notes, index)" tile>remove note</v-btn>
              </v-list-item>
              <v-textarea v-model="note.note" label="Note" :rules="[required]" filled dense></v-textarea>
              <v-divider></v-divider>
            </v-card-text>
          </template>
          <v-card-actions>
            <v-btn @click="addNote" color="primary" small tile>Add Customer Note</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <CardActions @close="show=false"  @submit="save" :disable_submit="licensesCheckResults.length > 0"></CardActions>
  </v-card>
</v-dialog>
</template>

<script>
export default {

  name: "CreateCustomer",
  data:() => ({
    show: false,
    debouncer: null,
    licensesCheckResults: [],
    form:{
      primary_name: null,
      customer_type_id: null,
      environment_type_id: null,
      contract_type_id: null,
      names: [],
      licenses: [],
      notes: [],
      contacts:[
        {
          contact_type_id: null,
          name: null,
          email: null,
          alt_email: null,
          phone_numbers: [],
          subscriptions: []
        }
      ],
      address:{
        address_type_id: null,
        region_type_id: null,
        street: null,
        city: null,
        state: null,
        zipcode: null,
        zipcodepf: null,
        county: "Unknown",
        lat: 1,
        lon: 1,
        user_id: 1,
      }
    },
    required: v => !!v || "This is a required field."
  }),
  created(){
    this.$store.$db().model('licenseTypes').api().fetch();
    this.$store.$db().model('environmentTypes').api().fetch();
    this.$store.$db().model('contractTypes').api().fetch();
    this.$store.$db().model('contactTypes').api().fetch();
    this.$store.$db().model('addressTypes').api().fetch();
    this.$store.$db().model('regionTypes').api().fetch();
    this.$store.$db().model('noteTypes').api().fetch();
    this.$store.$db().model('nameTypes').api().fetch();
  },
  computed:{
    addressRule(){
      return v => ((this.form.address.address_type_id !== 1) || (this.form.address.address_type_id === 1 && !!v))  || "This field is required on a physical address"
    }
  },
  methods:{
    debounceLicensesCheck(){
      if(this.debouncer){
        clearTimeout(this.debouncer);
      }
      this.debouncer = setTimeout(() => {
        this.checkLicense();
      }, 500);
    },
    async checkLicense(){
      this.licensesCheckResults = [];
      if(this.form.licenses.length === 0){
        return;
      }
      const query = await this.$store.$db().model('licenses').api().fetch({
        with: 'customer.primaryName',
        where_in: {
          key: 'license',
          values: this.form.licenses.map(license => license.license)
        }
      }, "",null,false,false);
      this.$set(this, 'licensesCheckResults', query.response.data)
      // this.licensesCheckResults = query.response.data;
    },
    addName(){
      this.form.names.push({name: null, name_type_id: null})
    },
    addLicense(){
      this.form.licenses.push({
        license: null,
        license_type_id: null,
        user_id: 1,
      })
    },
    addContact(){
      this.form.contacts.push({
        contact_type_id: null,
        name: null,
        email: null,
        alt_email: null,
        phone_numbers: [],
        subscriptions: []
      })
    },
    addPhoneNumber(index){
      this.form.contacts[index].phone_numbers.push({number: null, ext: null})
    },
    addNote(){
      this.form.notes.push({note: null, note_type_id: null})
    },
    async save(){
      if(this.$refs.form.validate()){
        let failSaves = [];
        let customer;
        try{
          customer = await this.saveCustomer();
        }catch(error){
          await this.$store.dispatch("showError", "Failed to store customer.");
          return false;
        }
        const customer_id = customer.response.data.id;
        try{
          await this.saveNames(customer_id)
        }catch(error){
          failSaves.push("names");
        }
        try{
          await this.saveLicenses(customer_id)
        }catch(error){
          failSaves.push("licenses");
        }
        try{
          await this.saveEnvironment(customer_id)
        }catch(error){
          failSaves.push("environment");
        }
        try{
          await this.saveContract(customer_id)
        }catch(error){
          failSaves.push("contract");
        }
        try{
          await this.saveAllContacts(customer_id)
        }catch(error){
          console.log(error);
          failSaves.push("contacts");
        }
        try{
          await this.saveAddress(customer_id)
        }catch(error){
          console.log(error);
          failSaves.push("address");
        }
        try{
          await this.saveNote(customer_id)
        }catch(error){
          failSaves.push("notes");
        }
        if(failSaves.length > 0){
          this.$store.dispatch("showError", "Failed to save: "+ failSaves.join(','))
        }else{
          this.$store.dispatch("showSuccess", "Saved Customer!")
        }
        this.resetForm();
        this.$emit("saved", customer_id);
      }
    },
    resetForm(){
      this.form = {
        primary_name: null,
        customer_type_id: null,
        environment_type_id: null,
        contract_type_id: null,
        names: [],
        licenses: [],
        notes: [],
        contacts:[
          {
            contact_type_id: null,
            name: null,
            email: null,
            alt_email: null,
            phone_numbers: [],
            subscriptions: []
          }
        ],
        address:{
          address_type_id: null,
          region_type_id: null,
          street: null,
          city: null,
          state: null,
          zipcode: null,
          zipcodepf: null,
          county: "Unknown",
          lat: null,
          lon: null,
          user_id: 1,
        }
      }
    },
    // all saves
    async saveCustomer(){
      return await this.$store.$db().model('customers').api().save({
        customer_type_id: this.form.licenses.length > 1 ? 1 : 2,
        user_id: 1,
      })
    },
    async saveNames(customer_id){
      await this.$store.$db().model('names').api().save({
        customer_id: customer_id,
        name: this.form.primary_name,
        name_type_id: 1,
        user_id: 1
      })
      for(let i in this.form.names){
        await this.$store.$db().model('names').api().save({
          customer_id: customer_id,
          name: this.form.names[i].name,
          name_type_id: this.form.names[i].name_type_id,
          user_id: 1
        })
      }
    },
    async saveLicenses(customer_id){
      for(let i in this.form.licenses){
        await this.$store.$db().model('licenses').api().save({
          customer_id: customer_id,
          ...this.form.licenses[i]
        })
      }
    },
    async saveEnvironment(customer_id){
      await this.$store.$db().model('environments').api().save({
        customer_id: customer_id,
        environment_type_id: this.form.environment_type_id,
        user_id: 1
      })
    },
    async saveContract(customer_id){
      await this.$store.$db().model('contracts').api().save({
        customer_id: customer_id,
        contract_type_id: this.form.contract_type_id,
        user_id: 1,
      })
    },
    async saveAllContacts(customer_id){
      for(let i in this.form.contacts){
        let contact = await this.saveContact(customer_id, this.form.contacts[i]);
        const contact_id = contact.response.data.id;
        await this.saveSubscriptions(contact_id, this.form.contacts[i]);
        await this.savePhoneNumbers(contact_id, this.form.contacts[i]);
      }
    },
    async saveContact(customer_id, contact){
      return await this.$store.$db().model('contacts').api().save({
        customer_id: customer_id,
        name: contact.name,
        email: contact.email,
        alt_email: contact.alt_email,
        contact_type_id: contact.contact_type_id,
        user_id: 1
      })
    },
    async saveSubscriptions(contact_id, contact){
      for(let i in contact.subscriptions){
        await this.$store.$db().model('subscriptions').api().save({
          contact_id: contact_id,
          email_type_id: contact.subscriptions[i],
          user_id: 1
        })
      }
    },
    async savePhoneNumbers(contact_id, contact){
      for(let i in contact.phone_numbers){
        await this.$store.$db().model('numbers').api().save({
          contact_id: contact_id,
          user_id: 1,
          ...contact.phone_numbers[i]
        })
      }
    },
    async saveAddress(customer_id){
      await this.$store.$db().model('addresses').api().save({
        customer_id: customer_id,
        user_id: 1,
        ...this.form.address
      })
    },
    async saveNote(customer_id){
      for(let i in this.form.notes){
        await this.$store.$db().model('notes').api().save({
          customer_id: customer_id,
          user_id: 1,
          ...this.form.notes[i]
        })
      }
    }
  },
}
</script>

<style scoped>

</style>
